/**
 * 极练 Rehab & Fit —— 疲劳引擎与恢复状态数据查询层（阶段 8）。
 *
 * 铁律与设计：
 * 1. 薄封装与纯函数聚合，连接 IndexedDB 与 @jilian/core 的 evaluateFatigueAccumulation 引擎；
 * 2. 纯函数聚合逻辑可完全独立单测；
 * 3. 严格遵循口径：仅统计已完成与主动提前结束的会话，高强度组按 RIR ≤ 1 过滤，辅助动作不跨口径混算容量。
 */

import {
  evaluateFatigueAccumulation,
  type DailyReadinessRecord,
  type DailyWorkoutLoad,
  type FatigueEngineResult,
} from "@jilian/core";
import type { WorkoutSession, WorkoutExercise, WorkoutSet } from "@jilian/shared";
import { db } from "../db/db";

/**
 * 纯函数：由会话、动作条目、组记录聚合近 42 天的 DailyWorkoutLoad[]。
 */
export function aggregateDailyWorkoutLoads(
  sessions: ReadonlyArray<WorkoutSession>,
  items: ReadonlyArray<WorkoutExercise>,
  sets: ReadonlyArray<WorkoutSet>,
  referenceDate: string,
): DailyWorkoutLoad[] {
  const ref = referenceDate.slice(0, 10);
  const validSessions = sessions.filter(
    (s) => (s.status === "completed" || s.status === "ended_early") && s.localDateAtStart <= ref,
  );

  const itemsBySession = new Map<string, WorkoutExercise[]>();
  for (const it of items) {
    const list = itemsBySession.get(it.sessionId) ?? [];
    list.push(it);
    itemsBySession.set(it.sessionId, list);
  }

  const setsByItem = new Map<string, WorkoutSet[]>();
  for (const s of sets) {
    const list = setsByItem.get(s.workoutExerciseId) ?? [];
    list.push(s);
    setsByItem.set(s.workoutExerciseId, list);
  }

  // 按日期归属汇总
  const dateMap = new Map<
    string,
    { completedSets: number; highIntensitySetsCount: number; durationMinutes: number; volumeKg: number }
  >();

  for (const sess of validSessions) {
    const d = sess.localDateAtStart;
    const sessItems = itemsBySession.get(sess.id) ?? [];

    let completedSets = 0;
    let highIntensitySetsCount = 0;
    let volumeKg = 0;

    for (const item of sessItems) {
      const itemSets = setsByItem.get(item.id) ?? [];
      for (const s of itemSets) {
        if (s.status !== "completed") continue;
        completedSets++;

        // RIR ≤ 1 视为极限/高强度组（含 RIR 0 力竭）
        if (s.rir !== null && s.rir <= 1) {
          highIntensitySetsCount++;
        }

        // 粗略容量指标（排除 assistance 辅助器械口径；仅作负荷粗略参考，不用于正式进阶）
        if (s.weightUnitScope !== "assistance" && s.actualWeightKg != null && s.reps != null) {
          volumeKg += s.actualWeightKg * s.reps;
        }
      }
    }

    // 训练时长计算
    let durationMinutes = 45; // 默认基准
    if (sess.startedAt && sess.finishedAt) {
      const diffMs = new Date(sess.finishedAt).getTime() - new Date(sess.startedAt).getTime();
      if (diffMs > 0) {
        durationMinutes = Math.max(10, Math.min(180, Math.round(diffMs / 60000)));
      }
    }

    const prev = dateMap.get(d) ?? { completedSets: 0, highIntensitySetsCount: 0, durationMinutes: 0, volumeKg: 0 };
    dateMap.set(d, {
      completedSets: prev.completedSets + completedSets,
      highIntensitySetsCount: prev.highIntensitySetsCount + highIntensitySetsCount,
      durationMinutes: prev.durationMinutes + durationMinutes,
      volumeKg: prev.volumeKg + volumeKg,
    });
  }

  return Array.from(dateMap.entries()).map(([localDate, data]) => ({
    localDate,
    volumeKg: Math.round(data.volumeKg * 10) / 10,
    completedSets: data.completedSets,
    highIntensitySetsCount: data.highIntensitySetsCount,
    durationMinutes: data.durationMinutes,
  }));
}

/**
 * 纯函数：从会话快照中提取每日准备度主观指标记录。
 */
export function extractDailyReadiness(sessions: ReadonlyArray<WorkoutSession>): DailyReadinessRecord[] {
  const records: DailyReadinessRecord[] = [];
  for (const s of sessions) {
    if (!s.readinessSnapshot || !s.localDateAtStart) continue;
    // 检查是否有直接的主观输入或原因码
    const snap = s.readinessSnapshot;
    let fatigueLevel: "low" | "medium" | "high" = "low";
    let sleepQuality: "good" | "moderate" | "poor" = "good";
    const muscleSoreness: "none" | "mild" | "significant" =
      snap.reasonCodes.includes("SORENESS_SIGNIFICANT")
        ? "significant"
        : snap.reasonCodes.includes("SORENESS_MILD")
          ? "mild"
          : "none";

    if (snap.reasonCodes.includes("MULTIPLE_RECOVERY_WARNINGS")) {
      fatigueLevel = "high";
      sleepQuality = "poor";
    } else if (snap.reasonCodes.includes("ONE_RECOVERY_WARNING")) {
      fatigueLevel = "medium";
    }

    records.push({
      localDate: s.localDateAtStart,
      fatigueLevel,
      sleepQuality,
      muscleSoreness,
    });
  }
  return records;
}

/**
 * 计算自上次减量以来的训练周数。
 */
export function calculateWeeksSinceLastDeload(
  lastDeloadWeekStartAt: string | null | undefined,
  referenceDate: string,
  sessions: ReadonlyArray<WorkoutSession>,
): number {
  if (lastDeloadWeekStartAt) {
    const t1 = new Date(`${referenceDate.slice(0, 10)}T00:00:00Z`).getTime();
    const t2 = new Date(`${lastDeloadWeekStartAt.slice(0, 10)}T00:00:00Z`).getTime();
    const diffWeeks = Math.floor((t1 - t2) / (7 * 86_400_000));
    return Math.max(0, diffWeeks);
  }

  // 无记录时：根据历史训练会话的最早日期估算连续周数（上限按 8 周截断）
  const valid = sessions.filter((s) => s.status === "completed" || s.status === "ended_early");
  if (valid.length === 0) return 0;
  const sorted = [...valid].sort((a, b) => a.localDateAtStart.localeCompare(b.localDateAtStart));
  const firstDate = sorted[0]!.localDateAtStart;
  const t1 = new Date(`${referenceDate.slice(0, 10)}T00:00:00Z`).getTime();
  const t2 = new Date(`${firstDate.slice(0, 10)}T00:00:00Z`).getTime();
  const diffWeeks = Math.floor((t1 - t2) / (7 * 86_400_000));
  return Math.max(0, Math.min(8, diffWeeks));
}

/**
 * 完整加载并计算用户的疲劳引擎评估结果。
 */
export interface UserFatigueStatus {
  result: FatigueEngineResult;
  hasEnoughData: boolean;
  totalCompletedSessions: number;
  isDeloadActive: boolean;
  deloadActiveUntil: string | null;
  deloadDaysRemaining: number;
}

export async function loadUserFatigueStatus(userId: string, referenceDate: string): Promise<UserFatigueStatus> {
  const [sessions, lastDeloadMeta, deloadUntilMeta] = await Promise.all([
    db.sessions.where("userId").equals(userId).toArray(),
    db.meta.get("lastDeloadWeekStartAt"),
    db.meta.get("deloadActiveUntil"),
  ]);

  const validSessions = sessions.filter((s) => s.status === "completed" || s.status === "ended_early");
  const totalCompletedSessions = validSessions.length;
  const hasEnoughData = totalCompletedSessions >= 2;

  // 恢复周生效期检查
  const today = referenceDate.slice(0, 10);
  const activeUntil = (deloadUntilMeta?.value as string | undefined) ?? null;
  const isDeloadActive = Boolean(activeUntil && today <= activeUntil);
  let deloadDaysRemaining = 0;
  if (isDeloadActive && activeUntil) {
    const tNow = new Date(`${today}T00:00:00Z`).getTime();
    const tEnd = new Date(`${activeUntil}T00:00:00Z`).getTime();
    deloadDaysRemaining = Math.max(1, Math.round((tEnd - tNow) / 86_400_000) + 1);
  }

  if (!hasEnoughData) {
    const emptyResult = evaluateFatigueAccumulation({
      referenceDate: today,
      historyLoads: [],
      historyReadiness: [],
      weeksSinceLastDeload: 0,
    });
    return {
      result: emptyResult,
      hasEnoughData: false,
      totalCompletedSessions,
      isDeloadActive,
      deloadActiveUntil: activeUntil,
      deloadDaysRemaining,
    };
  }

  const sessionIds = validSessions.map((s) => s.id);
  const items = await db.exerciseItems.where("sessionId").anyOf(sessionIds).toArray();
  const itemIds = items.map((i) => i.id);
  const sets = await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray();

  const historyLoads = aggregateDailyWorkoutLoads(validSessions, items, sets, today);
  const historyReadiness = extractDailyReadiness(validSessions);
  const weeksSinceLastDeload = calculateWeeksSinceLastDeload(
    lastDeloadMeta?.value as string | undefined,
    today,
    validSessions,
  );

  const result = evaluateFatigueAccumulation({
    referenceDate: today,
    historyLoads,
    historyReadiness,
    weeksSinceLastDeload,
  });

  return {
    result,
    hasEnoughData: true,
    totalCompletedSessions,
    isDeloadActive,
    deloadActiveUntil: activeUntil,
    deloadDaysRemaining,
  };
}

/** 开启为期 7 天的主动恢复周 */
export async function startDeloadWeek(todayDate: string): Promise<void> {
  const d = new Date(`${todayDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6); // 包含今天共 7 天
  const activeUntil = d.toISOString().slice(0, 10);

  const now = new Date().toISOString();
  await Promise.all([
    db.meta.put({ key: "lastDeloadWeekStartAt", value: todayDate.slice(0, 10), updatedAt: now }),
    db.meta.put({ key: "deloadActiveUntil", value: activeUntil, updatedAt: now }),
  ]);
}

/** 提前结束恢复周 */
export async function cancelDeloadWeek(): Promise<void> {
  await db.meta.delete("deloadActiveUntil");
}

/** 延期一周评估 */
export async function deferDeloadWeek(todayDate: string): Promise<void> {
  const d = new Date(`${todayDate.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 7);
  const deferredUntil = d.toISOString().slice(0, 10);
  await db.meta.put({
    key: "lastDeloadWeekStartAt",
    value: deferredUntil,
    updatedAt: new Date().toISOString(),
  });
}
